import { useNavigate } from 'react-router-dom';
import { ExternalLink, MessageSquare, Phone, MapPin } from 'lucide-react';
import type { JobWithRelations } from '@/api/jobs.api';
import { PanelLateral } from '@/components/common/PanelLateral';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatPhoneNumber } from '@/lib/format';

/**
 * A6 · La vista rápida de un TRABAJO.
 *
 * Qué entra acá y qué no: entra **lo que alguien mira antes de decidir si abrir
 * la ficha**. Si hubiera que abrir la ficha igual para saber lo básico, el panel
 * no sirvió de nada y encima costó un clic.
 *
 * Las acciones del pie son las tres que se hacen desde el listado: llamar al
 * cliente, abrir su conversación y ver la ficha completa. **No hay acciones que
 * cambien el estado del trabajo**, y es a propósito: cerrar o cancelar un
 * trabajo pide contexto —checklist, ubicación, monto— que en un panel de
 * resumen no está. Ofrecerlas acá sería invitar a hacerlas sin mirar.
 */

/** Lo que se muestra de un trabajo, en el orden en que se pregunta. */
function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  if (children == null || children === '') return null;
  return (
    <div className="border-b border-surface-border py-2 last:border-0 dark:border-slate-800">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{etiqueta}</dt>
      <dd className="mt-0.5 text-sm text-slate-900 dark:text-slate-100">{children}</dd>
    </div>
  );
}

export function VistaRapidaTrabajo({
  job,
  onClose,
  formatoFecha,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
  /** Cómo formatea fechas la pantalla. Se pasa para no tener dos formatos. */
  formatoFecha: (v: string | Date | null | undefined) => string;
}) {
  const navigate = useNavigate();
  if (!job) return null;

  const cliente = job.contact?.name?.trim() || job.contact?.pushName?.trim() || 'Sin nombre';
  const telefono = job.contact?.phone ?? null;

  return (
    <PanelLateral
      abierto
      onClose={onClose}
      titulo={cliente}
      subtitulo={job.descripcion ?? undefined}
      pie={
        <>
          {telefono && (
            <Button variant="outline" size="sm" onClick={() => window.open(`tel:${telefono}`)}>
              <Phone className="mr-1 h-4 w-4" />
              Llamar
            </Button>
          )}
          {job.conversationId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/conversations/${job.conversationId}`)}
            >
              <MessageSquare className="mr-1 h-4 w-4" />
              Ver la conversación
            </Button>
          )}
          <Button size="sm" onClick={() => navigate(`/jobs?abrir=${job.id}`)}>
            <ExternalLink className="mr-1 h-4 w-4" />
            Abrir la ficha completa
          </Button>
        </>
      }
    >
      <dl>
        <Dato etiqueta="Estado">
          <Badge>{job.estado}</Badge>
          {job.esGarantia && <Badge variant="warning" className="ml-1">Garantía</Badge>}
          {job.esRemoto && <Badge className="ml-1">Remoto</Badge>}
        </Dato>
        <Dato etiqueta="Técnico">{job.employee?.nombre ?? 'Sin asignar'}</Dato>
        <Dato etiqueta="Cita">{formatoFecha(job.scheduledAt)}</Dato>
        <Dato etiqueta="Teléfono">{telefono ? formatPhoneNumber(telefono) : null}</Dato>
        <Dato etiqueta="Zona">{job.zona}</Dato>
        <Dato etiqueta="Dirección">
          {job.direccion ? (
            <span className="flex items-start gap-1">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              {job.direccion}
            </span>
          ) : null}
        </Dato>
        {/* Las notas del panel: es lo que el técnico ve, y quien mira desde acá
            necesita saber qué se le dijo antes de preguntar por qué no lo hizo. */}
        <Dato etiqueta="Indicaciones">{job.notas}</Dato>
        <Dato etiqueta="Cerrado">{job.completedAt ? formatoFecha(job.completedAt) : null}</Dato>
        <Dato etiqueta="Monto del cierre">
          {typeof job.montoCierre === 'number' ? job.montoCierre.toLocaleString('es-DO') : null}
        </Dato>
      </dl>
    </PanelLateral>
  );
}
